import React, { useEffect, useState } from "react";
import { FaTrashAlt } from "react-icons/fa"; 

// Componentes comunes
import ListHeader from "../../../components/listing/ListHeader";
import ListFooter from "../../../components/listing/ListFooter";
// IMPORTANTE: Importamos DynamicTable
import DynamicTable from "../../../components/listing/DynamicTable";

// Sub-componentes
import FiltroCertificadoCliente from "./FiltroCertificadoCliente";
import CrearCertificadoCliente from "./CrearCertificadoCliente";
import EliminarCertificadoCliente from "./EliminarCertificadoCliente";

import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";

export default function ListarCertificadoCliente() {
  // ================================
  //   PAGINACIÓN
  // ================================
  const [porPagina, setPorPagina] = useState(30);
  const [pagina, setPagina] = useState(1);
  const [totalBackend, setTotalBackend] = useState(0);

  // ================================
  //   DATA TABLA PRINCIPAL
  // ================================
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ================================
  //   CACHE PARA EL MODAL (PREFETCH)
  // ================================
  const [cacheCertificados, setCacheCertificados] = useState([]);
  const [cacheClientes, setCacheClientes] = useState([]);

  // Modales
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [mostrarCrear, setMostrarCrear] = useState(false);
  const [registroAEliminar, setRegistroAEliminar] = useState(null);

  // ================================
  //   FILTROS
  // ================================
  const [filtros, setFiltros] = useState({
    id: "",
    certificadoId: "",
    clienteId: ""
  });

  const [reloadKey, setReloadKey] = useState(0);

  // ================================
  //   1. CARGA DE LA TABLA (PRINCIPAL)
  // ================================
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        let baseUrl = apiRoutes.certificadoCliente.listUrl({});

        if (baseUrl.includes("?")) {
          baseUrl = baseUrl.split("?")[0];
        }

        const queryParams = new URLSearchParams();
        queryParams.append("page", pagina - 1);
        queryParams.append("size", porPagina);

        // Mapeo de Filtros
        if (filtros.id) queryParams.append("id", filtros.id);
        if (filtros.certificadoId) queryParams.append("certificadoId", filtros.certificadoId);
        if (filtros.clienteId) queryParams.append("clienteId", filtros.clienteId);

        const url = `${baseUrl}?${queryParams.toString()}`;
        const res = await appFetchJson(url);

        // Adaptación para PageImpl (content) o listas planas (items)
        const items = Array.isArray(res?.content) ? res.content : (Array.isArray(res?.items) ? res.items : []);
        setData(items);

        const total =
          res?.totalElements ??
          res?.total ??
          (Array.isArray(items) ? items.length : 0);

        setTotalBackend(total);

      } catch (e) {
        console.error("Error cargando relaciones Certificado-Cliente:", e);
        setError("Error al cargar los datos.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [pagina, porPagina, filtros, reloadKey]);

  // ================================
  //   2. PRECARGA DE CATÁLOGOS (BACKGROUND)
  // ================================
  useEffect(() => {
    const cargarCatalogosBackground = async () => {
      try {
        const urlCert = apiRoutes.certificados?.listUrl 
             ? apiRoutes.certificados.listUrl({ size: 3000 })
             : `${apiRoutes.API_BASE || ''}/certificados/list?size=3000`;

        const urlCli = apiRoutes.clientes?.listUrl
             ? apiRoutes.clientes.listUrl({ size: 3000 })
             : `${apiRoutes.API_BASE || ''}/clientes/list?size=3000`;

        // Peticiones en paralelo
        const [resCert, resCli] = await Promise.all([
          appFetchJson(urlCert),
          appFetchJson(urlCli)
        ]);

        const certs = Array.isArray(resCert.content) ? resCert.content : (resCert.items || []);
        const clis = Array.isArray(resCli.content) ? resCli.content : (resCli.items || []);

        setCacheCertificados(certs);
        setCacheClientes(clis);
        
      } catch (e) {
        console.warn("No se pudieron precargar los catálogos en segundo plano", e);
      }
    };

    cargarCatalogosBackground();
  }, []); 

  // ====================================================================
  // DEFINICIÓN DE COLUMNAS (TOTAL 100%)
  // ====================================================================
  const columns = [
    // 1. CERTIFICADO (35%)
    { 
      header: "Certificado", 
      accessor: "certificadoNumero",
      type: "text", 
      width: "35%", 
      cellClass: "font-medium text-gray-800 break-all" 
    },
    // 2. CLIENTE (50%) - Texto largo
    { 
      header: "Cliente", 
      accessor: "clienteNombre",
      type: "text", 
      width: "50%", 
      cellClass: "whitespace-normal break-words leading-tight text-gray-700"
    },
    // 3. ACCIÓN (15%)
    {
      header: "Acción",
      type: "action", 
      width: "15%",
      render: (it) => (
        <div className="flex justify-center items-center gap-2">
          <button
            className="text-gray-500 hover:text-red-600 p-1"
            title="Eliminar"
            onClick={() => setRegistroAEliminar(it)}
          >
            <FaTrashAlt />
          </button>
        </div>
      )
    }
  ];

  // ================================
  //   RENDER
  // ================================
  return (
    <div className="page">

      <ListHeader
        title="Relación Certificados - Clientes"
        filters={filtros}
        onFilterClick={() => setMostrarFiltros(true)}
        onClearFilters={() => {
          setFiltros({
            id: "",
            certificadoId: "",
            clienteId: ""
          });
          setPagina(1);
        }}
        showAdd={false}
        addLabel="Nuevo Registro"
        onAddClick={() => setMostrarCrear(true)}
        pageSize={porPagina}
        onPageSizeChange={(size) => {
          setPorPagina(size);
          setPagina(1);
        }}
      />

      {/* Loading / Error */}
      {loading && <div className="mt-2 text-sm text-center text-gray-600">Cargando datos...</div>}
      {error && <div className="mt-2 text-sm text-red-600">{error}</div>}

      {/* DRAWER FILTROS */}
      {mostrarFiltros && (
        <FiltroCertificadoCliente
          initialFilters={filtros}
          onSubmit={(nuevosFiltros) => {
            setFiltros(nuevosFiltros);
            setPagina(1);
            setMostrarFiltros(false);
          }}
          onClose={() => setMostrarFiltros(false)}
        />
      )}

      {/* === TABLA DINÁMICA === */}
      {!loading && (
        <DynamicTable 
          size="S" // 3 Columnas -> Se ve bien en tabla hasta en 768px ('md')
          data={data}
          columns={columns}
        />
      )}

      {/* FOOTER */}
      <ListFooter
        page={pagina}
        pageSize={porPagina}
        total={totalBackend}
        onPageChange={(p) => setPagina(p)}
      />

      {/* MODALES */}
      {mostrarCrear && (
        <CrearCertificadoCliente
          onClose={() => setMostrarCrear(false)}
          onCreated={() => {
            setReloadKey((k) => k + 1);
            setMostrarCrear(false);
          }}
          // --- AQUÍ PASAMOS LA DATA PRECARGADA ---
          listaCertificadosPreloaded={cacheCertificados}
          listaClientesPreloaded={cacheClientes}
          // ---------------------------------------
        />
      )}

      {registroAEliminar && (
        <div className="modal-backdrop" onClick={() => setRegistroAEliminar(null)}>
          <div className="modal-panel-md" onClick={(e) => e.stopPropagation()}>
            <EliminarCertificadoCliente
              registro={registroAEliminar}
              onClose={() => setRegistroAEliminar(null)}
              onDeleted={() => {
                setReloadKey((k) => k + 1);
                setRegistroAEliminar(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}